import {
    AmbientLight,
    Clock,
    Color,
    DirectionalLight,
    Mesh,
    PCFSoftShadowMap,
    PerspectiveCamera,
    Raycaster,
    Scene,
    SRGBColorSpace,
    Vector2,
    WebGLRenderer,
    SpotLight,
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GTAOPass } from "three/examples/jsm/postprocessing/GTAOPass.js";
import { IRoomSize } from "./interfaces";

import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { OutlinePass } from "three/examples/jsm/postprocessing/OutlinePass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";

import { Robot } from "./Robot";
import { StaticMesh } from "./StaticMesh";
import { onMouseDown, onMouseUp, onMouseMove, onStartDrag, onArmHover } from "./Interactions";
import { selectionEvents } from "./Events";
import { SphereHelper, TransformHelper } from "./Helpers";
import { ACESFilmicToneMapping } from "three";

import { EXRLoader } from "three/examples/jsm/loaders/EXRLoader.js";
import { PMREMGenerator, EquirectangularReflectionMapping, Texture } from "three";

export class GraphicService {
    static divRef: HTMLDivElement;
    static scene: Scene;
    static camera: PerspectiveCamera;
    static renderer: WebGLRenderer;

    static composer: EffectComposer;
    static outlineEffect: OutlinePass;
    static control: OrbitControls;
    static castableMeshes: Mesh[] = [];
    static armMeshes: Mesh[] = [];
    static raycaster = new Raycaster();
    static outlineColor: Color;
    static ambLight: AmbientLight;
    static spotLight: SpotLight;
    private static fillLight: DirectionalLight;
    private static rimLight: DirectionalLight;
    static transformHelper: TransformHelper;
    static sphereHelper = new SphereHelper();
    private static isShadow = true;
    private static renderPass: RenderPass;
    private static clock = new Clock();
    static gtaoEffect: GTAOPass;
    static logoSpotLight: SpotLight;
    static leftSpotLight: SpotLight;
    static rightSpotLight: SpotLight;
    static backSpotLight: SpotLight;

    static envMap: Texture | null = null;

    static init(
        divRef: HTMLDivElement,
        roomSize: IRoomSize,
        outlineHexColor = "#00FF00",
        onAnimate: () => void = () => {
            const delta = GraphicService.clock.getDelta();

            GraphicService.composer.render(delta);
        }
    ): void {
        GraphicService.divRef = divRef;
        GraphicService.scene = new Scene();
        const width = GraphicService.divRef.clientWidth;
        const height = GraphicService.divRef.clientHeight;
        const isMobile = width <= 960;
        const fov = isMobile ? 90 : 75;
        GraphicService.camera = new PerspectiveCamera(fov, width / height, 0.001, 100);
        GraphicService.camera.position.x = isMobile ? -2 : -1;
        GraphicService.camera.position.z = isMobile ? 2 : 1;
        GraphicService.camera.position.y = 3;

        GraphicService.renderer = new WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: "high-performance",
            preserveDrawingBuffer: true,
            logarithmicDepthBuffer: true,
        });

        GraphicService.renderer.setSize(width, height);
        GraphicService.renderer.setClearColor(0x000000, 0);
        GraphicService.renderer.setPixelRatio(Math.min(1.5, window.devicePixelRatio));
        GraphicService.renderer.outputColorSpace = SRGBColorSpace;
        GraphicService.renderer.shadowMap.enabled = GraphicService.isShadow;
        GraphicService.renderer.shadowMap.type = PCFSoftShadowMap;
        GraphicService.renderer.shadowMap.autoUpdate = true;
        GraphicService.renderer.shadowMap.needsUpdate = true;
        GraphicService.renderer.toneMapping = ACESFilmicToneMapping;
        GraphicService.renderer.toneMappingExposure = 0.6;

        GraphicService.divRef.appendChild(GraphicService.renderer.domElement);

        GraphicService.outlineColor = new Color(outlineHexColor);

        GraphicService.control = new OrbitControls(
            GraphicService.camera,
            GraphicService.renderer.domElement
        );

        GraphicService.control.maxPolarAngle = (9 * Math.PI) / 20;

        GraphicService.raycaster = new Raycaster();

        GraphicService.scene.add(GraphicService.sphereHelper);

        GraphicService.setupLighting(roomSize);
        GraphicService.setupEffects();

        GraphicService.renderer.setAnimationLoop(onAnimate);

        window.addEventListener("resize", GraphicService.onWindowResize, false);

        GraphicService.divRef.addEventListener("pointerdown", onMouseDown);
        GraphicService.divRef.addEventListener("pointerup", onMouseUp);
        GraphicService.divRef.addEventListener("pointermove", onMouseMove);
        GraphicService.divRef.addEventListener("pointermove", onArmHover);

        selectionEvents.on("startDrag", (id: string) => {
            onStartDrag(id);
        });
    }

    static async loadHDRI(path: string, useAsBackground = false): Promise<void> {
        if (!GraphicService.renderer || !GraphicService.scene) {
            throw new Error("GraphicService must be initialised before loading an HDRI.");
        }

        const pmremGenerator = new PMREMGenerator(GraphicService.renderer);
        pmremGenerator.compileEquirectangularShader();

        try {
            const texture = await new EXRLoader().loadAsync(path);

            texture.mapping = EquirectangularReflectionMapping;

            const renderTarget = pmremGenerator.fromEquirectangular(texture);
            const environmentMap = renderTarget.texture;

            if (GraphicService.envMap) {
                GraphicService.envMap.dispose();
            }

            GraphicService.envMap = environmentMap;
            GraphicService.scene.environment = environmentMap;
            GraphicService.scene.environmentIntensity = 2;

            if (useAsBackground) {
                GraphicService.scene.background = environmentMap;
            }

            texture.dispose();
        } finally {
            pmremGenerator.dispose();
        }
    }

    private static setupEffects() {
        const resolution = new Vector2(
            GraphicService.divRef.clientWidth,
            GraphicService.divRef.clientHeight
        );

        GraphicService.renderPass = new RenderPass(GraphicService.scene, GraphicService.camera);

        GraphicService.outlineEffect = new OutlinePass(
            resolution,
            GraphicService.scene,
            GraphicService.camera
        );

        GraphicService.outlineEffect.visibleEdgeColor = GraphicService.outlineColor;
        GraphicService.outlineEffect.hiddenEdgeColor = GraphicService.outlineColor;
        GraphicService.outlineEffect.edgeGlow = 0.8;
        GraphicService.outlineEffect.edgeStrength = 5;
        GraphicService.outlineEffect.edgeThickness = 1.5;

        const outputPass = new OutputPass();

        GraphicService.composer = new EffectComposer(GraphicService.renderer);

        GraphicService.gtaoEffect = new GTAOPass(
            GraphicService.scene,
            GraphicService.camera,
            GraphicService.divRef.clientWidth,
            GraphicService.divRef.clientHeight
        );

        GraphicService.gtaoEffect.enabled = false;

        const gtao = GraphicService.gtaoEffect as any;

        gtao.radius = 0.3;
        gtao.distanceExponent = 1;
        gtao.thickness = 0.5;
        gtao.distance = 2;
        gtao.samples = 16;

        GraphicService.composer.addPass(GraphicService.gtaoEffect);
        GraphicService.composer.addPass(GraphicService.renderPass);
        GraphicService.composer.addPass(GraphicService.outlineEffect);
        GraphicService.composer.addPass(outputPass);
    }

    private static setupLighting(roomSize: IRoomSize) {
        /**
         * Soft white ambient light
         * Raises overall brightness.
         */
        GraphicService.ambLight = new AmbientLight(0xffffff, 3);
        GraphicService.scene.add(GraphicService.ambLight);

        /**
         * Focused spotlight over the patient
         */
        GraphicService.spotLight = new SpotLight(
            0xffffff,
            5,
            roomSize.height * 3,
            Math.PI / 5,
            0.75,
            0.7
        );

        GraphicService.spotLight.castShadow = true;

        GraphicService.renderer.shadowMap.enabled = true;

        GraphicService.spotLight.position.set(0, roomSize.height * 1.8, 0);

        GraphicService.spotLight.target.position.set(0, 0, 0);

        GraphicService.spotLight.castShadow = true;

        GraphicService.spotLight.shadow.mapSize.set(2048, 2048);

        GraphicService.spotLight.shadow.camera.near = 0.1;
        GraphicService.spotLight.shadow.camera.far = roomSize.height * 2.2;

        GraphicService.spotLight.shadow.bias = -0.00005;
        GraphicService.spotLight.shadow.normalBias = 0.008;

        GraphicService.scene.add(GraphicService.spotLight);
        GraphicService.scene.add(GraphicService.spotLight.target);

        GraphicService.spotLight.shadow.camera.updateProjectionMatrix();

        /**
         * spotlight over the logo
         */
        GraphicService.logoSpotLight = new SpotLight(
            0xffffff,
            15,
            roomSize.height * 2,
            Math.PI / 1,
            0.9,
            2
        );

        GraphicService.logoSpotLight.position.set(
            0,
            roomSize.height - 0.05,
            -roomSize.length / 2 + 1.5
        );

        GraphicService.logoSpotLight.target.position.set(
            0,
            roomSize.height * 0.58,
            -roomSize.length / 2 + 0.02
        );

        GraphicService.logoSpotLight.castShadow = false;

        GraphicService.scene.add(GraphicService.logoSpotLight);
        GraphicService.scene.add(GraphicService.logoSpotLight.target);

        GraphicService.logoSpotLight.target.updateMatrixWorld();
    }

    static enableShadows(shadow: boolean): void {
        GraphicService.isShadow = shadow;

        if (GraphicService.spotLight) {
            GraphicService.spotLight.castShadow = shadow;
        }

        GraphicService.renderer.shadowMap.enabled = shadow;
    }

    static onWindowResize(): void {
        if (GraphicService.divRef) {
            const width = GraphicService.divRef.clientWidth;
            const height = GraphicService.divRef.clientHeight;

            GraphicService.camera.aspect = width / height;
            GraphicService.camera.updateProjectionMatrix();

            GraphicService.renderer.setSize(width, height);
            GraphicService.composer.setSize(width, height);
        }
    }

    static highlightObject(
        obj: Robot | StaticMesh | { SM_Patient: StaticMesh; SM_Bed: StaticMesh },
        hexColor = "#00FF00"
    ): boolean {
        GraphicService.outlineEffect.visibleEdgeColor = new Color(hexColor);
        GraphicService.outlineEffect.hiddenEdgeColor = new Color(hexColor);
        GraphicService.outlineEffect.selectedObjects = [];
        return GraphicService.addToHighlight(obj);
    }

    static clearHighlight(): void {
        GraphicService.outlineEffect.selectedObjects = [];
    }

    static addToHighlight(
        obj: Robot | StaticMesh | { SM_Patient: StaticMesh; SM_Bed: StaticMesh }
    ): boolean {
        if (obj instanceof Robot) {
            GraphicService.outlineEffect.selectedObjects = [obj.baseMesh, obj.armMesh];
            return true;
        }

        if (obj instanceof StaticMesh) {
            GraphicService.outlineEffect.selectedObjects = obj.meshes;
            return true;
        }

        if (obj as { SM_Patient: StaticMesh; SM_Bed: StaticMesh }) {
            GraphicService.outlineEffect.selectedObjects = obj.SM_Bed.meshes.concat(
                obj.SM_Patient.meshes
            );
            return true;
        }

        return false;
    }

    static captureScene(maxWidth = 450): string {
        const renderer = GraphicService.renderer;

        if (!renderer) {
            console.warn("Cannot capture scene because the renderer is not available.");
            return "";
        }

        const canvas = renderer.domElement;

        const width = canvas.width;
        const height = canvas.height;

        const scale = maxWidth / width;

        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = width * scale;
        tempCanvas.height = height * scale;

        const ctx = tempCanvas.getContext("2d");

        if (!ctx) return "";

        ctx.drawImage(canvas, 0, 0, tempCanvas.width, tempCanvas.height);

        return tempCanvas.toDataURL("image/jpeg", 0.8);
    }

    static async captureSceneBlob(maxWidth = 900): Promise<Blob | null> {
        const renderer = GraphicService.renderer;

        if (!renderer) {
            console.warn("Cannot capture scene because the renderer is not available.");
            return null;
        }

        const canvas = renderer.domElement;

        const width = canvas.width;
        const height = canvas.height;

        const scale = Math.min(1, maxWidth / width);
        const scaledWidth = Math.max(1, Math.round(width * scale));
        const scaledHeight = Math.max(1, Math.round(height * scale));

        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = scaledWidth;
        tempCanvas.height = scaledHeight;

        const ctx = tempCanvas.getContext("2d");
        if (!ctx) return null;

        ctx.drawImage(canvas, 0, 0, scaledWidth, scaledHeight);

        return new Promise((resolve) => {
            tempCanvas.toBlob((blob) => {
                resolve(blob);
            }, "image/png");
        });
    }

    static dispose(): void {
        GraphicService.renderer.setAnimationLoop(null);

        window.removeEventListener("resize", GraphicService.onWindowResize);

        if (GraphicService.divRef) {
            GraphicService.divRef.removeEventListener("pointerdown", onMouseDown);
            GraphicService.divRef.removeEventListener("pointerup", onMouseUp);
            GraphicService.divRef.removeEventListener("pointermove", onMouseMove);
            GraphicService.divRef.removeEventListener("pointermove", onArmHover);

            if (GraphicService.renderer.domElement.parentNode === GraphicService.divRef) {
                GraphicService.divRef.removeChild(GraphicService.renderer.domElement);
            }
        }

        GraphicService.composer?.dispose();

        if (GraphicService.envMap) {
            GraphicService.envMap.dispose();
            GraphicService.envMap = null;
        }

        // Dispose Three.js resources
        GraphicService.renderer.dispose();
        GraphicService.control.dispose();

        // Clear scene
        GraphicService.scene.clear();

        // Clear mesh arrays
        GraphicService.castableMeshes = [];
        GraphicService.armMeshes = [];

        // Null out statics so they don't hold references
        GraphicService.divRef = null!;
        GraphicService.scene = null!;
        GraphicService.camera = null!;
        GraphicService.renderer = null!;
        GraphicService.composer = null!;
        GraphicService.outlineEffect = null!;
        GraphicService.control = null!;
    }
}
